import type {
    BreakpointId,
    BuilderPresets,
    BuilderStyle,
    CoreExportFormat,
    ReferencesMap,
    ResolvedRefArray,
    StyleVariableValueValue,
    VariantSplit,
} from '@wix/document-services-types'
import type { CssFile } from '../../../types'
import type { CssDeclaration, CssRoot } from '../../utils/css/types'
import { generateCssFile } from '../../utils/css/cssFileBuilder'
import { buildCssDeclaration } from '../../utils/css/utils'
import { processVariantSplit, refArrayToVariantSplit } from '../../utils/variantUtils'
import { buildCssAst, type CssRecord } from '../../utils/css/ast/astBuilder'
import { traverseComponents } from '../../../traversal/traverse'
import type { ComponentNode, SerializedVariablesList } from '../../../traversal/types'
import _ from 'lodash'
import { buildSelector } from '../../utils/css/cssSelectors'
import { buildSpxReferenceDeclarations } from '../../utils/css/spxTransformer'
import { getBreakpointData } from '../../utils/breakpoints'

export const PRESET_NAME_PROPERTY = '--preset-name'

export interface ResolvedGlobalStyle {
    id: string
    type: 'GlobalStyle'
    styleType: string
    value?: ResolvedRefArray<BuilderStyle>
}

interface GlobalStylesCollection {
    type: 'GlobalStylesCollection'
    styles: ResolvedGlobalStyle[]
}

const buildCssPropertyDeclaration = (key: string, value: string): CssDeclaration =>
    buildCssDeclaration(_.kebabCase(key), value)

const buildCssCustomPropertyDeclaration = (key: string, value: string): CssDeclaration =>
    buildCssDeclaration(`--${key}`, value)

const buildStyleDeclarations = (style: BuilderStyle): CssDeclaration[] => [
    ..._.map(buildSpxReferenceDeclarations(style.spx), (value, key) => buildCssDeclaration(key, value)),
    ..._.map(style.cssProperties, (value, key) => buildCssPropertyDeclaration(key, value)),
    ..._.map(style.cssCustomProperties, (value, key) => buildCssCustomPropertyDeclaration(key, value)),
]

const collectStyleRecords = (
    selector: string,
    style: BuilderStyle | string | undefined,
    breakpointId?: BreakpointId,
): CssRecord[] => {
    if (!style || _.isString(style)) {
        return []
    }

    const declarations = buildStyleDeclarations(style)
    if (declarations.length === 0) {
        return []
    }

    return [{ selector, breakpointId, type: 'style', declarations }]
}

const collectVariantStyleRecords = (selector: string, style: ComponentNode['style'] | undefined): CssRecord[] => {
    const records: CssRecord[] = []

    processVariantSplit(style, {
        onDefault: value => records.push(...collectStyleRecords(selector, value)),
        onVariant: (breakpointId, value) => records.push(...collectStyleRecords(selector, value, breakpointId)),
    })

    return records
}

const collectVariableRecords = (selector: string, variables: SerializedVariablesList | undefined): CssRecord[] => {
    if (!variables?.variables.length) {
        return []
    }

    const defaultDeclarations: CssDeclaration[] = []
    const variantDeclarations: Record<string, CssDeclaration[]> = {}

    for (const { cssVarName, value } of variables.variables) {
        if (!cssVarName) {
            continue
        }

        processVariantSplit(value, {
            onDefault: val => {
                const cssValue = val?.value as StyleVariableValueValue
                if (cssValue) {
                    defaultDeclarations.push(buildCssCustomPropertyDeclaration(cssVarName, cssValue))
                }
            },
            onVariant: (breakpointId, val) => {
                const cssValue = val?.value as StyleVariableValueValue
                if (cssValue) {
                    if (!variantDeclarations[breakpointId]) {
                        variantDeclarations[breakpointId] = []
                    }
                    variantDeclarations[breakpointId].push(buildCssCustomPropertyDeclaration(cssVarName, cssValue))
                }
            },
        })
    }

    const records: CssRecord[] = []

    if (defaultDeclarations.length > 0) {
        records.push({ selector, type: 'variable', declarations: defaultDeclarations })
    }

    for (const [breakpointId, declarations] of Object.entries(variantDeclarations)) {
        if (declarations.length > 0) {
            records.push({ selector, breakpointId, type: 'variable', declarations })
        }
    }

    return records
}

const collectPresetRecords = (selector: string, presets: VariantSplit<BuilderPresets> | undefined): CssRecord[] => {
    const records: CssRecord[] = []

    processVariantSplit(presets, {
        onDefault: value => {
            if (value?.presetName) {
                records.push({
                    selector,
                    type: 'style',
                    declarations: [buildCssDeclaration(PRESET_NAME_PROPERTY, value.presetName)],
                })
            }
        },
        onVariant: (breakpointId, value) => {
            if (value?.presetName) {
                records.push({
                    selector,
                    breakpointId,
                    type: 'style',
                    declarations: [buildCssDeclaration(PRESET_NAME_PROPERTY, value.presetName)],
                })
            }
        },
    })

    return records
}

export const getStyleRecords = (componentExport: CoreExportFormat): CssRecord[] => {
    const records: CssRecord[] = []

    traverseComponents(componentExport, {
        onComponent: ({ id, component, traverseInnerElements }) => {
            const selector = buildSelector({ type: 'component', componentId: id })
            records.push(...collectVariantStyleRecords(selector, component.style))
            records.push(...collectPresetRecords(selector, component.presets))
            records.push(...collectVariableRecords(selector, component.variables))
            traverseInnerElements(({ componentId, element, path }) => {
                const ieSelector = buildSelector({ type: 'innerElement', componentId, elementPath: path })
                records.push(...collectVariantStyleRecords(ieSelector, element.style))
                records.push(...collectPresetRecords(ieSelector, element.presets))
            })
        },
    })

    return records
}

export const generateCssAst = (componentExport: CoreExportFormat): CssRoot =>
    buildCssAst(getStyleRecords(componentExport), getBreakpointData(componentExport))

export const generateStyle = (componentExport: CoreExportFormat): CssFile =>
    generateCssFile(generateCssAst(componentExport))

const filterVariablesList = (
    variables: SerializedVariablesList | undefined,
    referencedVariables?: Record<string, unknown>,
): SerializedVariablesList | undefined => {
    if (!variables || !referencedVariables) {
        return variables
    }
    const referencedIds = new Set(_.keys(referencedVariables))
    return {
        ...variables,
        variables: variables.variables.filter(v => referencedIds.has(v.id)),
    }
}

const filterGlobalStyles = (
    styles: ResolvedGlobalStyle[],
    referencedStyles?: Record<string, unknown>,
): ResolvedGlobalStyle[] => {
    if (!referencedStyles) {
        return styles
    }
    const referencedIds = new Set(_.keys(referencedStyles))
    return styles.filter(s => referencedIds.has(s.id))
}

export const getGlobalStyleRecords = (componentExport: CoreExportFormat, references?: ReferencesMap): CssRecord[] => {
    const records: CssRecord[] = []
    const variableFilter = references ? references.variables ?? {} : undefined
    const styleFilter = references ? references.style ?? {} : undefined

    traverseComponents(componentExport, {
        onComponent: ({ id, component }) => {
            const selector = buildSelector({ type: 'component', componentId: id })
            records.push(...collectVariableRecords(selector, filterVariablesList(component.variables, variableFilter)))
        },
    })

    const globalStylesCollection = componentExport.globalData?.style?.GLOBAL_STYLES as
        | GlobalStylesCollection
        | undefined

    if (!globalStylesCollection?.styles) {
        return records
    }

    for (const globalStyle of filterGlobalStyles(globalStylesCollection.styles, styleFilter)) {
        const selector = buildSelector({ type: 'globalStyle', styleId: globalStyle.id })
        // remove this once variants core plugin is applied to global data
        const style = refArrayToVariantSplit(globalStyle.value)
        records.push(...collectVariantStyleRecords(selector, style))
    }

    return records
}

export const generateGlobalStyles = (componentExport: CoreExportFormat, references?: ReferencesMap): CssFile =>
    generateCssFile(buildCssAst(getGlobalStyleRecords(componentExport, references), {}))
